import React, { useEffect } from 'react'
import { observer } from "mobx-react-lite"

import styles from './LoopList.module.scss'
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import { TransitionGroup } from 'react-transition-group';
import Collapse from '@mui/material/Collapse';
import { useDispatch } from 'react-redux'
import { updatePresetsProgressions, updateMyProgressions } from '../editor/editorSlice'
import { createApi } from '../common/API'
import clsx from 'clsx'
import FilterBar from './FilterBar'
import LoopRow from './LoopRow'
import usePlayer from '../common/usePlayer';
// import { TipsProvider } from './ctx'
import { ShareDialog } from '../share/ShareDialog';
import useWindowSize from '../../hooks/window-size';
import Backdrop from '@mui/material/Backdrop';
import { useTipsEditor } from './ctx'
import userStore from '../common/userStore';

import { scroll } from '../../utils'

const queryPresetsProgression = createApi('/loop/queryPresetsProgression', {method: 'POST'})
const queryMyProgression = createApi('/loop/queryMyProgression', {method: 'POST'})

const RecycleBin = observer(({model}) => {
  return <>{model.deletedLoops.map(loop => (<Snackbar anchorOrigin={{vertical: 'bottom', horizontal: 'center'}} key={loop.id} open={true} message="Loop deleted" autoHideDuration={3000} 
    onClose={() => loop.deleteMyLoop()} 
    action={<Button color="primary" size="small" onClick={() => loop.undoRemove()}>
    UNDO
  </Button>} />))}
  </>
})

const stop = (e) => {
  // console.log(e)
  e.preventDefault()
  e.stopPropagation()
}

const hasRoot = (e) => {
  // console.log(e)
  const path = (e.composedPath && e.composedPath()) || e.path
  for (let idx in path) {
    let el = path[idx]
    if (el.id == 'root') {
      return true
    }
  }
  return false
}

const TipsBackdrop = observer(({model}) => {
  const { canShowTips } = useTipsEditor()
  const { width } = useWindowSize()
  return <Backdrop open={canShowTips && width > 768} onClick={stop}></Backdrop>
})

const LoopList = observer(({className, barStart, barEnd, showInstrument, showCover, top, model}) => {
  const loops = model.activeLoops
  const dispatch = useDispatch()
  const player = usePlayer()

  useEffect(() => {
    queryPresetsProgression().then(res => {
      if (res) {
        dispatch(updatePresetsProgressions(res))
      }
    })
    if (userStore.isAnonymous) {
      return;
    }
    queryMyProgression().then(res => {
      // console.log(res)
      if (res) {
        dispatch(updateMyProgressions(res))
      }
    })
  }, [userStore.isAnonymous])

  useEffect(() => {
    if (player.type != 'loop' && model.playingLoop) {
      model.playingLoop.stopPlay()
    }
  }, [model, player.type])

  useEffect(() => {

    const setActiveLoop = (e) => {
      //console.log('on', e)
      // if (!hasRoot(e)) {
      //   return;
      // }
      //console.log('to', e)

      if (model.loading || model.isEditorOpened) {
        return;
      }

      const loops = model.activeLoops

      if (loops.length == 0) {
        return;
      }

      const playingLoop = model.playingLoop
      if (e.keyCode === 32) { // Space
        if (playingLoop) {
          stop(e)
          playingLoop.togglePlaying()
        }
        return
      }
      if (e.keyCode === 13) { // Enter
        if (playingLoop) {
          stop(e)
          playingLoop.stopPlay()
          playingLoop.toOpen()
        }
        return
      }

      if (e.keyCode === 38) { // Up
        stop(e)
        if (playingLoop) {
          let idx = loops.findIndex((loop) => loop.active) - 1
          if (idx < 0) {
            if (model.page > 1) {
              playingLoop.stopPlay()
              model.setPage(model.page - 1)
              model.setAutoPlay('last')
            }
            return;
          }
          playingLoop.stopPlay()
          loops[idx].togglePlaying()
          return;
        }
        loops[loops.length - 1].togglePlaying()
        return
      }

      if (e.keyCode === 40) { // Down
        stop(e)
        if (playingLoop) {
          let idx = loops.findIndex((loop) => loop.active) + 1
          if (idx >= loops.length) {
            if (model.page < model.pageCount) {
              playingLoop.stopPlay()
              model.setAutoPlay('first')
              model.setPage(model.page + 1)
              // 滚动到顶部
              const currentY = document.documentElement.scrollTop || document.body.scrollTop
              scroll(currentY, 200)
            }
            return;
          }
          playingLoop.stopPlay()
          loops[idx].togglePlaying()
          return;
        }
        loops[0].togglePlaying()
      }
    }

    window.addEventListener('keydown', setActiveLoop, false)

    return () => {
      window.removeEventListener('keydown', setActiveLoop, false)
    }
  }, [model])

  return <>
      <Paper id='loops-list-paper' className={clsx(styles['root'], className)} elevation={0}>
        {top}
        <FilterBar start={barStart} end={barEnd} showInstrument={showInstrument} model={model}/>
        <div className={styles['filter-line']}></div>
        <div className={styles['list-header']}>
          {showCover ? <div className={styles['header-cover']}>Pack</div> : null}
          <div className={showCover ? styles['header-bars-cover'] : styles['header-bars']}>Bars</div>
          <div className={styles['header-process']}></div>
          <div className={styles['header-name']}>Name</div>
          <div className={styles['header-modified']}>Last Modified</div>
          <div className={styles['header-key']}>Key</div>
          <div className={styles['header-bpm']}>Bpm</div>
          <div className={styles['header-action']} style={{color: 'transparent'}}>&nbsp;</div>
        </div>
        <div className={clsx(styles['list-body'], {[styles['empty']]: model.empty})}>
          <TransitionGroup enter={!model.loading} exit={!model.loading} appear={!model.loading}>
          {loops.map((row, index) => {
            return <Collapse key={row.id}><LoopRow index={index + 1} model={row} showCover={showCover} /></Collapse>
          })}
          </TransitionGroup>
        </div>
      </Paper>
      {model.empty && model.page == 1 ? <div className={styles['empty-body']}>
        {model.isDefaulParams ? 'Your loops are empty. Add some presets or make your own.' : 'No loops  matched with specified filters.'}
      </div> : null}
      <div className={clsx(styles['pagination'], {[styles['empty']]: model.empty && model.page == 1})}>
        <Pagination page={model.page} onChange={(e, p) => model.setPage(p)} count={model.pageCount} 
          variant="outlined" color="primary" size="small" shape="rounded" 
          renderItem={item => <PaginationItem {...item} classes={{selected: styles['selected']}} />} />
      </div>
      <RecycleBin model={model} />
      <TipsBackdrop model={model} />
      <ShareDialog />
  </>
})

export default LoopList